import { Badge } from "@choose/ui/badge";
import { Button } from "@choose/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@choose/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@choose/ui/popover";
import { Separator } from "@choose/ui/separator";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CirclePlusIcon, PlusIcon, SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SnippetList } from "~/components/common/settings/snippets/snippet-list";
import { SnippetTagSelector } from "~/components/common/settings/snippets/snippet-tag-selector";
import { StoreSearchItem } from "~/components/layout/headers/tickets/store-search-item";
import { StoreSelector } from "~/components/layout/headers/tickets/store-selector";
import type { Store } from "~/graphql/graphql.gen";
import { useSnippetTags } from "~/hooks/use-snippet-tags";
import { useSnippetsFilters } from "~/stores/snippets-store";
import { getStores } from "~/stores/stores-store";

export const Route = createFileRoute(
  "/_auth/_app_shell/_settings-navigation/settings/snippets/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { filters, setFilters, clearFilters } = useSnippetsFilters();
  const { data: snippetTags } = useSnippetTags();
  const stores = getStores(t);

  const [openTag, setOpenTag] = useState<boolean>(false);
  const [openStore, setOpenStore] = useState<boolean>(false);

  const toggleSnippetTag = (snippetTagId: string) => {
    const { tagIds, ...rest } = filters;

    const newTagIds = tagIds.includes(snippetTagId)
      ? tagIds.filter((id) => id !== snippetTagId)
      : [...tagIds, snippetTagId];

    setFilters({
      ...rest,
      tagIds: newTagIds,
    });
  };

  const toggleStore = (storeId: Store) => {
    const { store, ...rest } = filters;

    const newStore = store.includes(storeId)
      ? store.filter((id) => id !== storeId)
      : [...store, storeId];

    setFilters({
      ...rest,
      store: newStore,
    });
  };

  return (
    <>
      <title>{`${t("settings.snippets.title")} - ${t("app_name")}`}</title>
      <div className="bg-background w-full overflow-auto">
        <div className="m-auto max-w-2xl p-8">
          <header className="mt-14">
            <div className="flex flex-row items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl">{t("settings.snippets.title")}</h1>
                <p className="text-muted-foreground mt-1 text-sm text-balance">
                  {t("settings.snippets.subtitle")}
                </p>
              </div>
              <div>
                <Button
                  type="button"
                  nativeButton={false}
                  render={
                    <Link to="/settings/snippets/add">
                      <PlusIcon />
                      {t("settings.snippets.add")}
                    </Link>
                  }
                />
              </div>
            </div>
          </header>
          <Separator className="my-8" />
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <SearchIcon />
                </InputGroupAddon>

                <InputGroupInput
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  placeholder={t("settings.snippets.search.placeholder")}
                />
                {filters.search ? (
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-xs"
                      onClick={() => setFilters({ ...filters, search: "" })}
                    >
                      <XIcon />
                    </InputGroupButton>
                  </InputGroupAddon>
                ) : null}
              </InputGroup>
              <div className="flex gap-2">
                <Popover open={openTag} onOpenChange={setOpenTag}>
                  <PopoverTrigger
                    render={
                      <Button variant="outline" size="sm" className="h-8">
                        <CirclePlusIcon />
                        {t("settings.snippets.tags")}
                        {filters.tagIds.length > 0 && (
                          <>
                            <Separator
                              orientation="vertical"
                              className="mx-2"
                            />
                            <Badge
                              variant="secondary"
                              className="rounded-sm px-1 font-normal lg:hidden"
                            >
                              {filters.tagIds.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                              {filters.tagIds.length > 2 ? (
                                <Badge
                                  variant="secondary"
                                  className="rounded-sm px-1 font-normal"
                                >
                                  {t("faceted_filter.x_selected", {
                                    count: filters.tagIds.length,
                                  })}
                                </Badge>
                              ) : (
                                snippetTags
                                  .filter((option) =>
                                    filters.tagIds.includes(option.id),
                                  )
                                  .map((option) => (
                                    <Badge
                                      variant="secondary"
                                      key={option.id}
                                      className="rounded-sm px-1 font-normal"
                                    >
                                      {option.label}
                                    </Badge>
                                  ))
                              )}
                            </div>
                          </>
                        )}
                      </Button>
                    }
                  />
                  <PopoverContent
                    className="w-fit min-w-[200px] p-0"
                    align="start"
                  >
                    <SnippetTagSelector
                      value={filters.tagIds}
                      onSelect={(snippetTagId, closeModal) => {
                        toggleSnippetTag(snippetTagId);
                        if (closeModal) {
                          setOpenTag(false);
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <Popover open={openStore} onOpenChange={setOpenStore}>
                  <PopoverTrigger
                    render={
                      <Button variant="outline" size="sm" className="h-8">
                        <CirclePlusIcon />
                        {t("settings.snippets.store")}
                        {filters.store.length > 0 && (
                          <>
                            <Separator
                              orientation="vertical"
                              className="mx-2"
                            />
                            <Badge
                              variant="secondary"
                              className="rounded-sm px-1 font-normal lg:hidden"
                            >
                              {filters.store.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                              {filters.store.length > 2 ? (
                                <Badge
                                  variant="secondary"
                                  className="rounded-sm px-1 font-normal"
                                >
                                  {t("faceted_filter.x_selected", {
                                    count: filters.store.length,
                                  })}
                                </Badge>
                              ) : (
                                stores
                                  .filter((option) =>
                                    filters.store.includes(option.value),
                                  )
                                  .map((option) => (
                                    <Badge
                                      variant="secondary"
                                      key={option.value}
                                      className="rounded-sm px-1 font-normal"
                                    >
                                      <div className="flex items-center gap-2">
                                        <StoreSearchItem store={option} />
                                      </div>
                                    </Badge>
                                  ))
                              )}
                            </div>
                          </>
                        )}
                      </Button>
                    }
                  />
                  <PopoverContent
                    className="w-fit min-w-[200px] p-0"
                    align="start"
                  >
                    <StoreSelector
                      value={filters.store}
                      onSelect={(storeId, closeModal) => {
                        toggleStore(storeId as Store);
                        if (closeModal) {
                          setOpenStore(false);
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {filters.tagIds.length > 0 || filters.store.length > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    {t("settings.snippets.clear")}
                    <XIcon />
                  </Button>
                ) : null}
              </div>
            </div>

            <SnippetList filters={filters} />
          </div>
        </div>
      </div>
    </>
  );
}
